import { createSelector } from 'reselect'
import { getSharedFacetNames, getSelectedFacets, getAllFacets } from 'selectors/algolia2/data'
import {
  doesSelectedFacetNameAttributeExist,
  getSelectedFacetNameAttributes,
  getSelectedFacetNameFirstAttribute,
} from 'selectors/algolia2/facets'
import { getPathParams, getPageParamsQuery } from './app'
import { breadcrumbFacets } from '../helpers/constants'
import { slugify } from 'lib/slugify'

/*
AllFacets - Algolia non filtered facet results ( all possible facets )
AlgoliaFacets - Algolia facets available after a query
SelectedFacets - Current Facets selected, used for building URL and Tracking changes
*/
export const getState = createSelector(
  (state: Record<string, any>) => ({ ...state }),
  (state): Record<string, any> => state
)
// Used for BreadCrumbs => ['all', 'abstract', 'photography']
export const getBreadcrumbFacets = createSelector(
  getState,
  (state): Array<string> =>
    breadcrumbFacets
      .reduce(
        (results, facetName) => [...results, getSelectedFacetNameFirstAttribute(facetName)(state)],
        []
      )
      .filter(Boolean)
)
// Used for Recreating Url => {category: 'All', styles: 'Abstract', medium: '', ...]
export const getFacetUrlParams = createSelector(
  [getPathParams, getState],
  (pathParams, state): Array<string> => {
    const urlPathParams = pathParams.reduce(
      (results, facetName) => ({
        ...results,
        [facetName]: getSelectedFacetNameFirstAttribute(facetName)(state) || '',
      }),
      {}
    )

    // Product Note -> restore URL rules for /prints instead of /prints/all
    if (urlPathParams.has_prints && urlPathParams.category === 'All') {
      delete urlPathParams.category
    }

    return urlPathParams
  }
)
// Used for creating PATH => ['all', 'abstract', 'photography']
export const getFacetValuesUrlParams = createSelector(
  [getPathParams, getFacetUrlParams],
  (pathParams, facetParams): Array<string> => {
    return pathParams
      .reduce((results, facetName) => [...results, facetParams[facetName]], [])
      .filter(Boolean)
  }
)
export const hasPrintsSelected = createSelector(
  [getSelectedFacetNameFirstAttribute('has_prints')],
  (hasPrints): boolean => !!hasPrints
)
export const getPrintType = createSelector(
  [hasPrintsSelected, getSelectedFacetNameAttributes('prints_availability')],
  (hasPrints, printsAvailability): string => {
    if (!hasPrints) {
      return ''
    }

    if (printsAvailability && printsAvailability.length === 1) {
      const [type] = printsAvailability
      return type
    }

    return 'all'
  }
)
export const getPrintTypeText = createSelector([getPrintType], (printType): string => {
  switch (printType) {
    case 'open':
      return 'Open Editions'

    case 'limited':
      return 'Limited Editions'

    case 'all':
      return 'Art Prints'

    default:
      return ''
  }
})
// We gather all DATA needed to create the URL using the HELPER => generateLocationUrl({ queryParams, pathParams, sharedFacetNames })
export const getParamsToGenerateURL = createSelector(
  [getSelectedFacets, getPageParamsQuery, getPathParams, getSharedFacetNames],
  (selectedFacets, pageParams, pathParams, sharedFacetNames): Record<string, any> => {
    return {
      queryParams: { ...selectedFacets, ...pageParams },
      pathParams,
      sharedFacetNames,
    }
  }
)
// We want a list of all selected facets => [{ facetName: string, attributeName: string }, ...]
export const getAllSelectedFacets = createSelector(
  [getSelectedFacets],
  (selectedFacets): Array<Record<string, any>> => {
    const selectedData = []
    Object.entries(selectedFacets).forEach(([facetName, attributes]) => {
      if (Array.isArray(attributes) && facetName !== 'has_prints') {
        attributes.forEach((attributeName) => {
          selectedData.push({
            facetName,
            attributeName,
            title: String(attributeName),
          })
        })
      } else {
        selectedData.push({
          facetName,
          attributeName: attributes,
          title: String(attributes),
        })
      }
    })
    return selectedData
  }
)
export const getSelectedFacetsCount = createSelector(
  [getAllSelectedFacets],
  (allSelectedFacets): number => {
    const pageNumber = allSelectedFacets.some((e) => e.facetName === 'page')
    const hitsPerPage = allSelectedFacets.some((e) => e.facetName === 'hitsPerPage')

    const isPageFacet = () => {
      if (pageNumber && hitsPerPage) return 2
      else if (pageNumber || hitsPerPage) return 1
      else return 0
    }

    const selectedFacetsLength = allSelectedFacets.length - 1 - isPageFacet() // -1 to remove has_print facet count
    return selectedFacetsLength
  }
)
// selector to check if any category is selected
export const getSelectedCategoryFacetLength = createSelector(
  [getSelectedFacets],
  (selectedFacets): number => selectedFacets.category?.length || 0
)
export const getSelectedCategoryFacet = createSelector(
  [getSelectedFacets],
  (selectedFacets): string => String(selectedFacets.category) || 'All'
)
export const getAllCategoryFacets = createSelector(
  [getAllFacets],
  (allFacets): Array<string> => Object.keys(allFacets?.category || {})
)
export const updateCategoryFacetValues = (facetName: string, attributeValue: string) =>
  createSelector(
    [
      getSelectedFacetNameAttributes(facetName),
      doesSelectedFacetNameAttributeExist(facetName, attributeValue),
    ],
    (selectedFacetNameValues, facetNameAttributeExist): Array<string> => {
      // special case for category facet
      // we always want to select only one category (that becomes part of the URL)
      // Ex: /paintings, /sculpture
      if (facetNameAttributeExist && Array.isArray(selectedFacetNameValues)) {
        return [...selectedFacetNameValues.filter((attr) => attr !== attributeValue)] // REMOVE AttributeValue is it exists already
      } else {
        selectedFacetNameValues = [] // remove all existing attributes

        return [...selectedFacetNameValues, attributeValue] // add the new Attribute value
      }
    }
  )
export const getSelectedCategoryRecCarousel = createSelector(
  [getSelectedFacets],
  (selectedFacets): string => {
    const { category } = selectedFacets

    if (String(selectedFacets.has_prints) === 'true') {
      return 'Printmaking'
    }

    return String(category) || 'All'
  }
)
export const getSelectedCategoryFacetName = createSelector(
  [getSelectedFacets],
  (selectedFacets): string => {
    const { has_prints, category } = selectedFacets
    const isPrintsPage = has_prints?.[0]
    if (Boolean(isPrintsPage)) {
      return 'prints'
    }

    const categoryValue = category?.[0]
    const categorySelected = slugify(categoryValue)

    return categorySelected || 'all'
  }
)
